import logger from '../../Utils/logger';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Header } from '../../Components/Header/Header';
import { MAX_GUESTS } from '../../Utils/bookingConfig';
import styles from './Style.module.css';
import AvatarCircleIcon from '../../Components/Icons/AvatarCircleIcon';
import PlusCircleIcon from '../../Components/Icons/PlusCircleIcon';
import { BtnSecondaryWithIcon } from '../../Components/Buttons/BtnWithIcon/BtnSecondaryWithIcon';
import EditPaxDropdown from '../../Components/Dropdowns/EditPaxDropdown/EditPaxDropdown';
import { ServiceAddonPopup } from '../../Components/Popup/ServiceAddonPopup/ServiceAddonPopup';
import Cart from '../../Components/Cart/Cart';
import { TherapistList } from '../../Components/TherapistList/TherapistList';
import DropdownIcon from '../../Components/Icons/DropdownIcon';
import MaleAvatarIcon from '../../Components/Icons/MaleAvatarIcon';
import FemaleAvatarIcon from '../../Components/Icons/FemaleAvatarIcon';
import { useSticky } from '../../Utils/Helper';
import { RemoveGuestPopup } from '../../Components/Popup/RemoveGuestPopup/RemoveGuestPopup';
import { Trash2 } from 'lucide-react';
import { useAppointment } from '../../Context/Appointment/AppointmentContext';
import { useUI } from '../../Context/UI/UIContext';
import { useGuests } from '../../Context/Guests/GuestsContext';
import { useSchedule } from '../../Context/Schedule/ScheduleContext';
import { removeSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { useValidateBookingSession } from '../../Utils/Hooks/useValidateBookingSession';
import { useCartDisplayTotal } from '../../Utils/Hooks/useCartDisplayTotal';
import { getPrimaryGuestName } from '../../Utils/guestMembers';
import { useLocation } from '../../Context/Location/LocationContext';
import { ServiceAddons } from './ServiceAddons';
import { AvailabilityPopup } from '../../Components/Popup/AvailabilityPopup/AvailabilityPopup';
import { useGuestAvailability } from '../../Utils/Hooks/useGuestAvailability';

export const GuestsAndServices = () => {
  useValidateBookingSession();
  const navigate = useNavigate();
  const { selectedOutletID, outletTypeID } = useLocation();
  const { appointmentTypeID, isGroupBooking, membership } = useAppointment();
  const { setIsCartActive } = useUI();
  const { members, addGuest, removeGuest, setActiveGuestId, updateMembers, selectedServices } = useGuests();
  const { setSelectedDate, setSelectedTime } = useSchedule();

  const [openMenuId, setOpenMenuId] = useState(null);
  const [removeGuestPopupActive, setRemoveGuestPopupActive] = useState(false);
  const [guestIdPendingRemove, setGuestIdPendingRemove] = useState(null);
  const [serviceAddonPopupActive, setServiceAddonPopupActive] = useState(false);
  const [isTherapistListShow, setIsTherapistListShow] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [selectedServiceDuration, setSelectedServiceDuration] = useState(null);
  const [selectedServiceTarget, setSelectedServiceTarget] = useState(null);
  const bottomAnchorRef = useRef(null);
  const prevLenRef = useRef(members.length);
  const { isSticky, sentinelRef } = useSticky();

  useEffect(() => {
    if (appointmentTypeID == null) {
      navigate("/");
    }
  }, [appointmentTypeID, navigate]);

  useEffect(() => {
    if (members.length > prevLenRef.current) {
      // A new guest was added - set the newly added guest as active
      const lastGuest = members[members.length - 1];
      if (lastGuest?.id) {
        setActiveGuestId(lastGuest.id);
      }
      bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
    prevLenRef.current = members.length;
  }, [members, setActiveGuestId]);

  useEffect(() => {
    if (openMenuId == null) return;
    const closeOutside = (e) => {
      if (!e.target.closest?.("[data-guest-menu]")) setOpenMenuId(null);
    };
    document.addEventListener("mousedown", closeOutside);
    return () => document.removeEventListener("mousedown", closeOutside);
  }, [openMenuId]);

  const goEditMember = useCallback(
    (index) => {
      const memberId = members[index]?.id;
      if (memberId) setActiveGuestId(memberId);
      navigate('/select-services');
    },
    [navigate, members, setActiveGuestId]
  );

  const handleAddGuest = useCallback(
    (e) => {
      e.stopPropagation();
      if (members.length >= MAX_GUESTS) return;
      try {
        // Add the new guest - addGuest() returns the new guest ID
        const newGuestId = addGuest();

        // addGuest should return the new guest's ID
        // Use flushSync to ensure the state update completes before navigation
        if (newGuestId != null) {
          flushSync(() => {
            setActiveGuestId(newGuestId);
          });
        }

        // Navigate to services with replace to avoid awkward back button behavior
        // (user shouldn't need to go back to the "add guest" moment)
        navigate('/select-services', { replace: true });
      } catch (error) {
        logger.error('Error adding guest:', error);
      }
    },
    [addGuest, members.length, navigate, setActiveGuestId]
  );

  const openRemoveGuestPopup = useCallback((guestId) => {
    setGuestIdPendingRemove(guestId);
    setRemoveGuestPopupActive(true);
  }, []);

  const closeRemoveGuestPopup = useCallback(() => {
    setRemoveGuestPopupActive(false);
    setGuestIdPendingRemove(null);
  }, []);

  const confirmRemoveGuestFromPopup = useCallback(() => {
    if (guestIdPendingRemove != null) {
      removeGuest(guestIdPendingRemove);
    }
    setOpenMenuId(null);
    closeRemoveGuestPopup();
  }, [guestIdPendingRemove, removeGuest, closeRemoveGuestPopup]);

  const cardTitle = (member, index) => {
    const customerName = (member.customerName || "").trim();
  
    if (member.isPrimary) {
      return getPrimaryGuestName();
    }
  
    return customerName || member.name || member.displayName || `Guest ${index + 1}`;
  };

  const selectedServiceForTherapist = useMemo(() => {
    if (!selectedServiceTarget) return null;
    const member = members.find((m) => m.id === selectedServiceTarget.memberId);
    return member?.services?.[selectedServiceTarget.serviceIndex] ?? null;
  }, [members, selectedServiceTarget]);

  const selectedAddonIds = useMemo(() => {
    if (!selectedServiceForTherapist) return [];
    return (selectedServiceForTherapist.selectedAddons || []).map((addon) => addon.id);
  }, [selectedServiceForTherapist]);

  const handleAddonSelect = useCallback(
    (addon) => {
      if (!selectedServiceTarget) return;
      updateMembers((prev) =>
        prev.map((member) => {
          if (member.id !== selectedServiceTarget.memberId) return member;
          return {
            ...member,
            services: (member.services || []).map((svc, idx) => {
              if (idx !== selectedServiceTarget.serviceIndex) return svc;
              const existing = svc.selectedAddons || [];
              const alreadySelected = existing.some((item) => item.id === addon.id);
              return {
                ...svc,
                selectedAddons: alreadySelected
                  ? existing.filter((item) => item.id !== addon.id)
                  : [...existing, addon],
              };
            }),
          };
        })
      );
    },
    [selectedServiceTarget, updateMembers]
  );

  const handleAddonRemove = useCallback(
    (memberId, serviceIndex, addonId) => {
      updateMembers((prev) =>
        prev.map((member) => {
          if (member.id !== memberId) return member;
          return {
            ...member,
            services: (member.services || []).map((svc, idx) => {
              if (idx !== serviceIndex) return svc;
              return {
                ...svc,
                selectedAddons: (svc.selectedAddons || []).filter((addon) => addon.id !== addonId),
              };
            }),
          };
        })
      );
    },
    [updateMembers]
  );

  const flatServices = useMemo(
    () => members.flatMap((member) => member.services || []),
    [members]
  );
  const cartDisplayCount = flatServices.length;
  const cartDisplayTotal = useCartDisplayTotal(members, membership);

  const onAvailabilityConfirmed = useCallback(() => setIsCartActive(true), [setIsCartActive]);
  const clearUnavailableServices = useCallback(() => {
    // Retain guest identities while removing services and their nested addons,
    // therapist assignments, and service times. Guest selectors reset totals.
    updateMembers((previous) => previous.map((member) => ({ ...member, services: [] })));
    setSelectedDate('');
    setSelectedTime('');
    removeSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE);
    removeSessionStorageValue('service_list');
    setSelectedServiceId(null);
    setSelectedServiceDuration(null);
    setSelectedServiceTarget(null);
  }, [updateMembers, setSelectedDate, setSelectedTime]);
  const {
    canContinue, availabilityError, serviceRefreshError,
    isCheckingAvailability, isRefreshingServices,
    handleContinue, handleChooseAnotherService,
  } = useGuestAvailability({ members, selectedOutletID, outletTypeID,
    onContinue: onAvailabilityConfirmed, onNoTherapists: clearUnavailableServices });

  const handleOpenTherapistList = useCallback(
    (memberId, service, serviceIndex) => {
      setSelectedServiceId(service?.id ?? null);
      setSelectedServiceDuration(service?.duration ?? null);
      setSelectedServiceTarget({
        memberId,
        serviceId: service?.id,
        serviceIndex,
      });
      setIsCartActive(false);
      setIsTherapistListShow(true);
    },
    [
      setIsCartActive,
      setIsTherapistListShow,
      setSelectedServiceDuration,
      setSelectedServiceId,
    ]
  );

  const handleSelectTherapist = useCallback(
    (therapistData) => {
      if (!selectedServiceTarget) return;
      const clearedTherapist = therapistData == null || therapistData?.id == null;
      updateMembers((prev) =>
        prev.map((member) => {
          if (member.id !== selectedServiceTarget.memberId) return member;
          return {
            ...member,
            services: (member.services || []).map((svc, idx) => {
              if (idx !== selectedServiceTarget.serviceIndex) {
                return svc;
              }
              return {
                ...svc,
                therapistData: clearedTherapist
                  ? null
                  : { ...therapistData, requested: true },
              };
            }),
          };
        })
      );
      removeSessionStorageValue(STORAGE_KEYS.DATE);
      removeSessionStorageValue(STORAGE_KEYS.DATE_SELECTION_SOURCE);
      removeSessionStorageValue(STORAGE_KEYS.TIME);
      setSelectedDate('');
      setSelectedTime('');
      setIsTherapistListShow(false);
      setIsCartActive(true);
    },
    [selectedServiceTarget, updateMembers, setIsCartActive, setSelectedDate, setSelectedTime]
  );


  // One effect owns the page's modal overlay and scroll lock.
  useEffect(() => {
    const body = document.body;
    const hasPopup = removeGuestPopupActive || serviceAddonPopupActive || Boolean(availabilityError);
    body.classList.remove('half', 'overlay-index');
    body.classList.toggle('overlay', hasPopup);
    body.classList.toggle('overflow-hidden', hasPopup || isTherapistListShow);
    return () => body.classList.remove('overlay', 'overflow-hidden');
  }, [removeGuestPopupActive, serviceAddonPopupActive, availabilityError, isTherapistListShow]);

  return (
    <>
    <div className="sticky-sentinel" ref={sentinelRef} aria-hidden="true" />
    <div className={`sticky-header ${isSticky ? "is-sticky" : ""}`}>
      <Header title="Guests & Services" />
    </div>
      <div className={`space-y ${styles.page}`}>
        {members.map((member, index) => {
          const memberServices = member.services ?? [];
          return (
          <section
            key={member.id}
            id={`guest-section-${member.id}`}
            className={`${styles.memberSection} container-md space-x`}
          >
            <div className={styles.memberCard}>
              <div className={styles.memberCardHeader}>
                <div className={styles.memberCardHeaderLeft}>
                  <AvatarCircleIcon size={42} />
                  <div className={styles.memberCardHeaderLeftContent}>
                    <h2 className={styles.memberTitle}>
                      {cardTitle(member, index)}
                    </h2>
                    <p className={styles.memberCardHeaderLeftServiceCount}>
                      {memberServices.length}{" "}
                      {memberServices.length === 1 ? "service" : "services"}
                    </p>
                  </div>
                </div>
                <div className={styles.memberCardHeaderRight}>

                <EditPaxDropdown
                  triggerAriaLabel="Edit services"
                  actions={[
                    {
                      id: "edit-services",
                      label: "Edit Services",
                      onClick: () => goEditMember(index),
                    },
                    ...(!member.isPrimary
                      ? [
                          {
                            id: "remove-guest",
                            label: "Remove Guest",
                            onClick: () => openRemoveGuestPopup(member.id),
                          },
                        ]
                      : []),
                  ]}
                />
                </div>
              </div>

              {(member.services ?? []).map((svc, svcIndex) => (
                <div key={`${member.id}-${svc.id}-${svcIndex}`} className={styles.serviceCard}>
                  <div className={styles.serviceName}>
                      {svc.duration} mins {svc.name}
                  </div>
                  {svc.is_addon === 1 && (
                  <ServiceAddons
                    serviceId={svc.id}
                    selectedAddonsCount={svc.selectedAddons?.length ?? 0}
                    onAddClick={() => {
                      setSelectedServiceId(svc.id);
                      setSelectedServiceTarget({
                        memberId: member.id,
                        serviceId: svc.id,
                        serviceIndex: svcIndex,
                      });
                      setServiceAddonPopupActive(true);
                    }}
                  >
                    {(svc.selectedAddons?.length ?? 0) > 0 ? (
                      <div className={styles.addonChipList}>
                        {svc.selectedAddons.map((addon) => (
                          <span key={addon.id} className={styles.addonChip}>
                            <span className={styles.addonChipText}>{addon.name}</span>
                            <button
                              type="button"
                              className={`${styles.addonChipDelete} btn`}
                              aria-label={`Remove ${addon.name}`}
                              title={`Remove ${addon.name}`}
                              onClick={() =>
                                handleAddonRemove(member.id, svcIndex, addon.id)
                              }
                            > <Trash2 size={16} color="var(--DangerColor)" /></button>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </ServiceAddons>
                  )}
                  <div className={`${styles.therapistDetails} ${styles.serviceCardContent}`}>
                    <p className={styles.label}>Therapist:</p>
                    <BtnSecondaryWithIcon 
                    label={(svc?.therapistData?.alias || "No Preference")} 
                    title={(svc?.therapistData?.alias || "No Preference")}
                    icon={
                      svc?.therapistData?.gender === "male" ? <MaleAvatarIcon size={16} />
                      : svc?.therapistData?.gender === "female" ? <FemaleAvatarIcon size={16} />
                      : null}
                    secondaryIcon={<DropdownIcon size={16} />}
                    variant="smallPill" 
                    customClass={styles.therapistSelectBtn}
                    handleClick={() => {
                      handleOpenTherapistList(member.id, svc, svcIndex);
                    }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
        })}

        {isGroupBooking && members.length < MAX_GUESTS ? (
          <div className={`${styles.addGuestBtn} container-md space-x`}>
          <BtnSecondaryWithIcon 
          label="Add Guest" 
          icon={<PlusCircleIcon size={16} />} 
          variant="smallPill" 
          handleClick={handleAddGuest} 
          title="Add Guest"
          />
          </div>
        ) : null}
      </div>

      <RemoveGuestPopup
        active={removeGuestPopupActive}
        onClose={closeRemoveGuestPopup}
        onConfirmRemove={confirmRemoveGuestFromPopup}
      />

      <ServiceAddonPopup
        active={serviceAddonPopupActive}
        handleClick={() => setServiceAddonPopupActive(false)}
        serviceId={selectedServiceId}
        selectedAddonIds={selectedAddonIds}
        onSelectAddon={handleAddonSelect}
      />

      <TherapistList
        active={isTherapistListShow}
        handleClick={() => {
          setIsTherapistListShow(false);
          setIsCartActive(true);
        }}
        serviceId={selectedServiceId}
        serviceDuration={selectedServiceDuration}
        onSelectTherapist={handleSelectTherapist}
        memberForTherapist={selectedServiceTarget?.memberId ?? null}
        serviceForTherapist={selectedServiceForTherapist}
        serviceIndexForTherapist={selectedServiceTarget?.serviceIndex ?? null}
      />

      {!isTherapistListShow ? (
        <Cart
          btnLabel={isCheckingAvailability ? 'Processing...' : 'Next'}
          btnClick={handleContinue}
          isCartActive
          disabled={!canContinue || isCheckingAvailability}
          displayTotal={cartDisplayTotal}
          displayCount={cartDisplayCount}
          selectedServices={selectedServices}
        />
      ) : null}
      {availabilityError && (
        <AvailabilityPopup
          message={availabilityError}
          onChooseService={handleChooseAnotherService}
          isLoading={isRefreshingServices}
          error={serviceRefreshError}
        />
      )}
    </>
  );
};
